<script>
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';
  import { analyticsStore, PreferenceState } from '$lib/analytics/store';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import { afterUpdate, onMount } from 'svelte';
  import Link from './Link.svelte';
  import { page } from '$app/stores';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  const unlockPage = () => {
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('pointer-events');
  };

  const lockPage = () => {
    document.body.style.overflow = 'hidden';
    document.body.style.pointerEvents = 'none';
  };

  const handleLock = () => {
    if (
      brandReskinEnabled &&
      $page.url.pathname !== '/cookie-policy' &&
      $analyticsStore.shouldRequestConsent === PreferenceState.Yes
    ) {
      lockPage();
    } else {
      unlockPage();
    }
  };

  onMount(() => {
    handleLock();
  });

  afterUpdate(() => {
    handleLock();
  });

  const responsiveVars = new ResponsiveVars()
    .addFluid('font-size', toFluidVarInput(12, 12))
    .addFluid('gap', toFluidVarInput(21, 0))
    .addFluid('button-gap', toFluidVarInput(21, 36))
    .addFluid('py', toFluidVarInput(24, 24))
    .addFluid('text-px', toFluidVarInput(27, 0))
    .addFluid('button-py', toFluidVarInput(9, 0))
    .toCSS();
</script>

{#if $analyticsStore.shouldRequestConsent === PreferenceState.Yes}
  {#if brandReskinEnabled}
    <div class={cx('container', 'banner--reskin', responsiveVars)}>
      <p>
        <FontSizeDecorator size="caption">
          We use cookies to gather basic analytics on how visitors use our website.
        </FontSizeDecorator>
      </p>
      <div class="actions">
        <button data-text="Accept" on:click={analyticsStore.acceptAnalytics}> Accept </button>
        <button data-text="Decline" on:click={analyticsStore.declineAnalytics}> Decline </button>
        <a data-text="Read more" href="/cookie-policy" rel="external"> Read more </a>
      </div>
    </div>
    {#if $page.url.pathname !== '/cookie-policy'}
      <div class="overlay" />
    {/if}
  {:else}
    <div class="banner--legacy">
      <p>
        We use cookies to gather basic analytics on how visitors use our website.
        <Link to="/cookie-policy" rel="external">Learn More.</Link>
      </p>
      <div class="actions">
        <Link as="button" on:click={analyticsStore.acceptAnalytics}>Yum, cookies!</Link>
        <Link as="button" on:click={analyticsStore.declineAnalytics}>Decline</Link>
      </div>
    </div>
  {/if}
{/if}

<style lang="scss">
  @import 'src/scss/media';

  .banner--legacy {
    position: fixed;
    bottom: 0;
    width: 100%;
    background: var(--twist-color-gray-100);
    padding: 24px 40px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    z-index: map-get($zIndex, cookie-banner);

    @include at('lg') {
      flex-direction: row;
      margin-bottom: var(--twist-core-spacing-0);
    }

    .actions {
      display: flex;
      gap: var(--twist-core-spacing-5);
      align-self: flex-end;
    }

    p {
      color: var(--twist-color-gray-0);
      font-family: var(--font-family-primary);
      font-size: var(--twist-font-size-0);
      line-height: 160%;
      font-weight: var(--ifm-font-weight-light);
      margin: 0 0 var(--twist-core-spacing-5);

      @include at('lg') {
        margin: var(--twist-core-spacing-0);
      }
    }
  }

  .banner--reskin {
    position: fixed;
    bottom: 0;
    width: 100%;
    background: var(--twist-color-gray-95);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: var(--gap);
    z-index: map-get($zIndex, cookie-banner);
    pointer-events: auto;

    @include at('lg') {
      flex-direction: row;
    }

    .actions {
      display: flex;
      gap: var(--button-gap);
      padding-bottom: var(--py);

      @include at('lg') {
        padding: 0;
      }
    }

    .actions button,
    .actions a {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      background: none;
      border: none;
      padding: var(--button-py) 0;
      font-size: var(--font-size);
      font-family: var(--font-family-primary);
      font-weight: 400;
      letter-spacing: 0.96px;
      color: #f2f2f2;
      text-transform: uppercase;
      text-decoration: none;

      &:hover,
      &:focus {
        font-weight: 600;
      }

      // prevents layout shift on hover due to font weight increase to semibold
      &::after {
        content: attr(data-text) / '';
        height: 0;
        visibility: hidden;
        overflow: hidden;
        user-select: none;
        pointer-events: none;
        font-weight: 600;

        @media speech {
          display: none;
        }
      }
    }

    p {
      color: #f2f2f2;
      padding: var(--py) var(--text-px) 0;
      text-align: center;

      @include at('lg') {
        padding: var(--py) 0;
      }
    }
  }

  .overlay {
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(242, 242, 242, 0.7);
    z-index: map-get($zIndex, cookie-banner-overlay);
    pointer-events: none;
  }
</style>
